
(() => {
  /*
   * Fyneshyt Quick Add
   *
   * Behavior:
   * - One Quick Add popup at a time.
   * - Clicking the same Quick Add button again does nothing.
   * - Clicking another product's Quick Add replaces the current popup.
   * - Rapid clicks always prioritize the latest product.
   * - Safe if this script is initialized more than once.
   * - Uses event delegation so dynamically rendered buttons work.
   */

  if (window.FyneshytQuickAddInitialized) return;

  window.FyneshytQuickAddInitialized = true;

  let activeQuickAdd = null;
  let activeProductHandle = null;
  let requestId = 0;

  /* ============================================================
     REMOVE ACTIVE QUICK ADD
     ============================================================ */

  const removeActiveQuickAdd = () => {
    if (!activeQuickAdd) {
      activeProductHandle = null;
      return;
    }

    const quickAdd = activeQuickAdd;

    activeQuickAdd = null;
    activeProductHandle = null;

    quickAdd.classList.remove("is-open");
    quickAdd.setAttribute("aria-hidden", "true");

    if (quickAdd.isConnected) {
      quickAdd.remove();
    }
  };

  /* ============================================================
     OPEN QUICK ADD
     ============================================================ */

  const openQuickAdd = async (button) => {
    const productHandle = button.dataset.productHandle;

    if (!productHandle) {
      return;
    }

    /*
     * If the same product is already open, do nothing.
     */
    if (
      activeQuickAdd &&
      activeProductHandle === productHandle
    ) {
      return;
    }

    /*
     * Every new click gets a new request ID.
     *
     * If the user clicks:
     *
     * Product A → Product B → Product C
     *
     * only Product C is allowed to finish and open.
     */
    const currentRequestId = ++requestId;

    /*
     * If another product's Quick Add is open,
     * remove it immediately.
     */
    if (activeQuickAdd) {
      removeActiveQuickAdd();
    }

    /*
     * Prevent multiple requests for the same click
     * from visually stacking.
     */
    button.setAttribute("aria-busy", "true");

    try {
      const response = await fetch(
        `/products/${encodeURIComponent(productHandle)}?view=quick-add`,
      );

      /*
       * Ignore an outdated request.
       */
      if (currentRequestId !== requestId) {
        return;
      }

      if (!response.ok) {
        throw new Error("Unable to load product");
      }

      const html = await response.text();

      /*
       * Ignore an outdated request after the network request.
       */
      if (currentRequestId !== requestId) {
        return;
      }

      const wrapper = document.createElement("div");

      wrapper.innerHTML = html;

      const quickAdd = wrapper.querySelector(
        "[data-quick-add]",
      );

      if (!quickAdd) {
        throw new Error(
          "Quick Add component not found",
        );
      }

      /*
       * Another request may have become the latest request
       * while the HTML was being parsed.
       */
      if (currentRequestId !== requestId) {
        return;
      }

      /*
       * Safety check.
       *
       * If something else opened a Quick Add between the
       * fetch and this point, replace it.
       */
      if (activeQuickAdd) {
        removeActiveQuickAdd();
      }

      activeQuickAdd = quickAdd;
      activeProductHandle = productHandle;

      /*
       * Start hidden.
       */
      quickAdd.setAttribute(
        "aria-hidden",
        "true",
      );

      document.body.appendChild(quickAdd);

      /*
       * Open animation.
       */
      requestAnimationFrame(() => {
        if (
          currentRequestId !== requestId ||
          activeQuickAdd !== quickAdd
        ) {
          return;
        }

        quickAdd.classList.add("is-open");

        quickAdd.setAttribute(
          "aria-hidden",
          "false",
        );
      });

      /* ==========================================================
         CLOSE QUICK ADD
         ========================================================== */

      let isClosing = false;

      const closeQuickAdd = () => {
        if (isClosing) {
          return;
        }

        isClosing = true;

        /*
         * Invalidate any pending request associated with
         * this modal.
         */
        requestId++;

        quickAdd.classList.remove("is-open");

        quickAdd.setAttribute(
          "aria-hidden",
          "true",
        );

        if (activeQuickAdd === quickAdd) {
          activeQuickAdd = null;
          activeProductHandle = null;
        }

        /*
         * Remove after the CSS close animation.
         */
        setTimeout(() => {
          if (quickAdd.isConnected) {
            quickAdd.remove();
          }
        }, 300);
      };

      /* ==========================================================
         CLOSE BUTTON
         ========================================================== */

      const closeButton = quickAdd.querySelector(
        "[data-quick-add-close]",
      );

      if (closeButton) {
        closeButton.addEventListener(
          "click",
          closeQuickAdd,
        );
      }

      /* ==========================================================
         CLICK OUTSIDE
         ========================================================== */

      quickAdd.addEventListener(
        "click",
        (event) => {
          if (event.target === quickAdd) {
            closeQuickAdd();
          }
        },
      );

      /* ==========================================================
         ESCAPE KEY
         ========================================================== */

      const handleEscape = (event) => {
        if (
          event.key === "Escape" &&
          activeQuickAdd === quickAdd
        ) {
          closeQuickAdd();
        }
      };

      document.addEventListener(
        "keydown",
        handleEscape,
      );

      /*
       * Clean up the Escape listener when the modal is removed.
       */
      const originalRemove =
        quickAdd.remove.bind(quickAdd);

      quickAdd.remove = () => {
        document.removeEventListener(
          "keydown",
          handleEscape,
        );

        if (activeQuickAdd === quickAdd) {
          activeQuickAdd = null;
          activeProductHandle = null;
        }

        originalRemove();
      };

      /* ==========================================================
         AJAX ADD TO CART
         ========================================================== */

      const form = quickAdd.querySelector(
        "[data-quick-add-form]",
      );

      if (form) {
        form.addEventListener(
          "submit",
          async (event) => {
            event.preventDefault();

            const submitButton =
              form.querySelector(
                '[type="submit"]',
              );

            if (
              !submitButton ||
              submitButton.disabled
            ) {
              return;
            }

            const originalText =
              submitButton.textContent.trim();

            submitButton.disabled = true;
            submitButton.textContent =
              "Adding...";

            try {
              const formData =
                new FormData(form);

              const cartResponse = await fetch(
                `${window.Shopify.routes.root}cart/add.js`,
                {
                  method: "POST",
                  headers: {
                    Accept:
                      "application/json",
                  },
                  body: formData,
                },
              );

              if (!cartResponse.ok) {
                throw new Error(
                  "Unable to add product to cart",
                );
              }

              await cartResponse.json();

              /* ==================================================
                 UPDATE CART COUNT
                 ================================================== */

              const cartStateResponse =
                await fetch(
                  `${window.Shopify.routes.root}cart.js`,
                );

              if (cartStateResponse.ok) {
                const cart =
                  await cartStateResponse.json();

                const cartCount =
                  document.querySelector(
                    "[data-cart-count]",
                  );

                if (cartCount) {
                  cartCount.textContent =
                    cart.item_count;

                  cartCount.hidden =
                    cart.item_count === 0;
                }
              }

              /* ==================================================
                 SUCCESS
                 ================================================== */

              submitButton.textContent =
                "Added ✓";

              /*
               * Notify other theme components.
               */
              document.dispatchEvent(
                new CustomEvent(
                  "cart:updated",
                ),
              );

              /*
               * Close after showing the success state.
               */
              setTimeout(() => {
                closeQuickAdd();
              }, 500);
            } catch (error) {
              console.error(
                "Fyneshyt Add to Cart:",
                error,
              );

              submitButton.disabled = false;

              submitButton.textContent =
                originalText ||
                "Try again";
            }
          },
        );
      }
    } catch (error) {
      /*
       * Don't show an error from an old request after
       * the user has already selected another product.
       */
      if (currentRequestId !== requestId) {
        return;
      }

      console.error(
        "Fyneshyt Quick Add:",
        error,
      );
    } finally {
      button.removeAttribute("aria-busy");
    }
  };

  /* ============================================================
     EVENT DELEGATION
     ============================================================ */

  document.addEventListener(
    "click",
    (event) => {
      const button = event.target.closest(
        "[data-quick-add-open]",
      );

      if (!button) {
        return;
      }

      event.preventDefault();

      openQuickAdd(button);
    },
  );
})();

